import notes from '@/sui/data/sheets/edit/notes'
import tools from '@/sui/data/sheets/edit/tools'
import patient from '@/sui/data/sheets/edit/patient'

import units from '@/sui/data/units'

import { goFieldsToJS, goTreeToJS } from '@/sui/data/sheets/edit/fromGo'

import ice from '@/sui/tools/ice'
import date from '@/sui/tools/date'
import assert from '@/sui/tools/assert'
import go from '@/gojs/go'

import moment from 'moment'

function getCollectedUnitsLabel (type, uf, unit) {
  assert.isNumber(type)
  assert.isString(uf)
  assert.isString(unit)

  const cvps = `Détails pour les CVPs : ${type === 1 ? 'OUI' : 'NON'}`
  return `${uf} - ${unit} (${cvps})`
}

function extractPreFilledUnits (vm) {
  assert.notNull(vm)

  const result = []
  const sheets = vm.sheets
  assert.isArray(sheets)
  const done = new Set()
  const s = vm.surveillance
  const types = units[s.year].list

  for (let i = 0; i !== sheets.length; ++i) {
    const sheet = sheets[i]
    if (sheet.uf != null && sheet.uf !== '' && sheet.unitType != null) {
      const unit = types.find(e => e.index === sheet.unitType)
      assert.notNull(unit)

      const key = getCollectedUnitsLabel(sheet.type, sheet.uf, unit.value)
      if (!done.has(key)) {
        done.add(key)

        result.push(
          {
            value: key,
            code: {
              uf: sheet.uf,
              unitType: unit.code,
              cvps: sheet.type
            }
          }
        )
      }
    }
  }

  return result
}

function selectInitialPreFilledUnitsIFP (vm, list) {
  assert.isArray(list)
  assert.isObject(vm.sheet)

  if (list.length === 0) {
    return -1
  }

  const uf = vm.sheet[fields.uf.field]
  const unitType = vm.sheet[fields.unitType.field]
  const cvps = vm.sheet[fields.includeCVPs.field]

  const emptyUf = (uf === '' || uf == null)
  const emptyUnitType = unitType == null
  const emptyCVPs = cvps == null
  const sum = emptyUnitType + emptyUf + emptyCVPs
  if (sum === 3) {
    return null
  }

  if (sum > 0) {
    return -1
  }

  const types = units[vm.surveillance.year].list
  const unit = types.find(e => e.code === unitType)
  const key = getCollectedUnitsLabel(cvps, uf, unit.value)
  const result = list.find(l => l.value === key)

  return result.code
}

const fields = (function () {
  const result = goFieldsToJS(
    go.spy4.v2026.fields,
    ['unitType', 'sheetNumber', 'uf']
  )
  return result
})()

const catheterFields = fields.catheters.fields

fields.sheetNumber = tools.opts(
  patient.sheetNumber.v2,
  { srv: go.spy4.v2026.fields.sheetNumber.sfield }
)

fields.collectedUnits = tools.cfg.list(
  'collectedUnits', 'Identifiant du service', [], {
    sp: 'none',

    ini: function (vm) {
      const list = extractPreFilledUnits(vm)
      this.config.values = [
        { value: 'Nouveau service', code: -1 },
        ...list
      ]

      const initial = selectInitialPreFilledUnitsIFP(vm, list)
      if (initial != null) {
        this.commit(vm, initial, vm.sheet)
      }

      this.readOnly = list.length === 0
    },

    cm: function (vm, e, target) {
      vm.$set(target, this.field, e)
      if (e !== -1) {
        vm.$set(target, fields.uf.field, e[fields.uf.field])
        vm.$set(target, fields.unitType.field, e[fields.unitType.field])
        vm.$set(target, fields.includeCVPs.field, e[fields.includeCVPs.field])
      } else {
        vm.$set(target, fields.uf.field, null)
        vm.$set(target, fields.unitType.field, null)
        vm.$set(target, fields.includeCVPs.field, null)
      }
    }
  }
)

fields.unitType = tools.opts(
  patient.unitType.v2.rw, {
    srv: go.spy4.v2026.fields.unitType.sfield,
    sp: 'unitType_v2',
    show: tools.equals(fields.collectedUnits.field, -1)
  }
)

fields.uf = tools.opts(
  patient.uf, {
    srv: go.spy4.v2026.fields.uf.sfield,
    op: false,
    show: tools.equals(fields.collectedUnits.field, -1)
  }
)

fields.includeCVPs = tools.opts(
  fields.includeCVPs, {
    show: tools.equals(fields.collectedUnits.field, -1)
  }
)

const tree = goTreeToJS(
  go.spy4.v2026.tree, fields
)
tree.splice(1, 0, fields.collectedUnits)

const titles = {
  patient: {
    text: 'DONNÉES ADMINISTRATIVES DU PATIENT',
    start: fields.collectedUnits.field
  },

  implementation: {
    text: 'TRAÇABILITÉ DE LA POSE',
    type: 'subtitle',
    start: catheterFields.operatorNameFound.field
  },

  decision: {
    text: 'DÉCISION POUR UNE VOIE VEINEUSE AU MOMENT DE LA POSE',
    type: 'subtitle',
    start: catheterFields.venousLineNecessary.field
  },

  treatments: {
    text: 'TRAITEMENTS PERFUSÉS DEPUIS LA POSE',
    type: 'subtitle',
    start: catheterFields.irritatingSolutionUsed.field
  },

  clinical: {
    text: 'TRAÇABILITÉ DE LA SURVEILLANCE CLINIQUE',
    type: 'subtitle',
    start: catheterFields.traceability.field
  },

  relevance: {
    text: `JUSTIFICATION DU MAINTIEN DU CATHETER LE JOUR DE L'ENQUÊTE`,
    type: 'subtitle',
    start: catheterFields.relevance.field
  }
}

function checkUf (result, sheet, year) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.isNumber(year)

  const uf = sheet[fields.uf.field]
  const ufNote = result[fields.uf.field]
  assert.notNull(ufNote)

  if (uf == null || uf.length >= 3 || ufNote.type != null) {
    return
  }

  ufNote.type = 'error'
  ufNote.tooltip = `Le nom de l'UF doit contenir au moins 3 caractères.`
}

function checkDates (result, sheet, year) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.isNumber(year)

  const dates = notes.collectDates(
    result, sheet, [
      fields.surveyDate,
      fields.birthdate,
      fields.entry
    ]
  )

  const oldest = notes.oldest[`${year}`]
  const newest = notes.newest[`${year}`]
  const start = notes.start[`${year}`]
  const now = notes.now()

  const comparisons = [
    { f: start, s: dates.surveyDate },
    { f: oldest, s: dates.birthdate },
    { f: dates.birthdate, s: dates.entry },
    { f: dates.birthdate, s: dates.surveyDate },
    { f: dates.entry, s: dates.surveyDate },
    { f: dates.surveyDate, s: now },
    { f: dates.surveyDate, s: newest }
  ]

  const kts = sheet[fields.catheters.field]
  const ktsNotes = result[fields.catheters.field]
  assert.equals(kts.length, ktsNotes.length)

  for (let i = 0; i !== kts.length; ++i) {
    const kt = kts[i]
    const note = ktsNotes[i]
    const insertionDate = notes.dateField(
      note, kt, catheterFields.insertionDate
    )
    comparisons.push(
      { f: dates.birthdate, s: insertionDate },
      { f: insertionDate, s: dates.surveyDate }
    )
  }
  notes.compareDates(comparisons)
}

function checkBirthdateAndUnit (result, sheet, year) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.isNumber(year)

  const unitType = getUnitTypeCodeAndNote(result, sheet, year)
  const birthdate = sheet[fields.birthdate.field]
  const birthdateNote = result[fields.birthdate.field]
  if (unitType == null || birthdate == null ||
      (unitType.note.type != null && birthdateNote.type != null)) {
    return
  }

  const now = moment()
  const mb = moment(birthdate, 'DD/MM/YYYY')
  const age = now.diff(mb, 'years')

  let message = ''
  if (age > 18 && unitType.value.match(/^PED/) != null) {
    message = `Incohérence possible : le patient semble trop âgé pour être dans un service de pédiatrie.`
  } else if (age > 1 && unitType.value.match(/^NEO/) != null) {
    message = `Incohérence possible : le  patient semble trop âgé pour être dans un service de néonatalogie.`
  }

  if (message !== '') {
    if (unitType.note.type == null) {
      unitType.note.type = 'warning'
      unitType.note.tooltip = message
    }

    if (birthdateNote.type == null) {
      birthdateNote.type = 'warning'
      birthdateNote.tooltip = message
    }
  }
}

function getUnitTypeCodeAndNote (result, sheet, year) {
  const collectedUnits = sheet[fields.collectedUnits.field]
  if (collectedUnits != null && collectedUnits !== -1) {
    return {
      note: result[fields.collectedUnits.field],
      value: units[year].codeFromIndex(collectedUnits.unitType)
    }
  }

  const unitType = sheet[fields.unitType.field]
  if (unitType != null) {
    return {
      note: result[fields.unitType.field],
      value: unitType
    }
  }

  return null
}

function checkCatheter (result, sheet, year, unitMatch, expectedType, message) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.isNumber(year)
  assert.isRegExp(unitMatch)
  assert.isString(expectedType)
  assert.isString(message)

  const unitType = getUnitTypeCodeAndNote(result, sheet, year)
  if (unitType == null || unitType.value.match(unitMatch) != null) {
    return
  }

  const list = []
  const kts = sheet[fields.catheters.field]
  const ktsNotes = result[fields.catheters.field]
  assert.equals(kts.length, ktsNotes.length)
  for (let i = 0; i !== kts.length; ++i) {
    const kt = kts[i]
    const note = ktsNotes[i]

    const type = kt[catheterFields.type.field]
    if (type == null || type !== expectedType) {
      continue
    }
    list.push(note[catheterFields.type.field])
  }

  if (list.length === 0) {
    return
  }

  unitType.note.type = 'warning'
  unitType.note.tooltip = message

  for (let i = 0; i !== list.length; ++i) {
    const note = list[i]
    note.type = unitType.note.type
    note.tooltip = unitType.note.tooltip
  }
}

function checkCVO (result, sheet, year) {
  checkCatheter(
    result, sheet, year, /^(NEO|MIX)/, 'C3',
    `Incohérence possible : les CVO sont habituellement présents dans les services de néonatalogie.`
  )
}

function checkECMO (result, sheet, year) {
  checkCatheter(
    result, sheet, year, /REA/, 'C6',
    `Incohérence possible : les ECMO sont habituellement présents dans les services de réanimation.`
  )
}

function checkCatheterDuration (result, sheet, year) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.isNumber(year)

  const surveyDate = sheet[fields.surveyDate.field]
  if (surveyDate == null) {
    return
  }

  const maxDuration = {
    'C8': 90,
    'C9': 90,
    'C10': 30,
    'C11': 30
  }

  const kts = sheet[fields.catheters.field]
  const ktsNotes = result[fields.catheters.field]
  for (let i = 0; i !== kts.length; ++i) {
    const kt = kts[i]
    const note = ktsNotes[i]

    const type = kt[catheterFields.type.field]
    const duration = maxDuration[type]
    const insertionDate = kt[catheterFields.insertionDate.field]
    if (duration == null || insertionDate == null) {
      continue
    }

    const lapse = date.getElapsed(surveyDate, insertionDate, 'days')
    if (lapse == null || lapse <= duration) {
      continue
    }

    const impactedNotes = [
      result[fields.surveyDate.field],
      note[catheterFields.insertionDate.field],
      note[catheterFields.type.field]
    ]
    for (let j = 0; j !== impactedNotes.length; ++j) {
      const current = impactedNotes[j]
      if (current.type != null) {
        continue
      }
      current.type = 'warning'
      current.tooltip = `Incohérence possible : la durée de cathétérisme (${lapse} jours) semble élevée pour ce type de cathéter.`
    }
  }
}

function buildNotes2026 (result, sheet, surveillance) {
  checkUf(result, sheet, 2026)
  checkDates(result, sheet, 2026)
  checkBirthdateAndUnit(result, sheet, 2026)
  checkCVO(result, sheet, 2026)
  checkECMO(result, sheet, 2026)
  checkCatheterDuration(result, sheet, 2026)
  return result
}

const sheet = {
  titles: [
    titles.patient
  ],

  repeatableTitles: [
    titles.implementation,
    titles.decision,
    titles.treatments,
    titles.clinical,
    titles.relevance
  ],

  fields: tree,
  buildNotes: buildNotes2026
}

const result = {
  fields,
  titles,
  sheet
}

export default ice.freeze(result, 10)
